import { useRequiredContext } from "@redotech/react-util/context";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { ConversionPermissions } from "@redotech/redo-model/permissions";
import { LinkPrimaryTab } from "@redotech/redo-web/arbiter-components/navigation/primary/redo-primary-nav";
import { PageNavigationEntry } from "@redotech/redo-web/arbiter-components/navigation/redo-side-navigation";
import { SecondaryNavigationData } from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-secondary-nav";
import { useNavTreeNode } from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-secondary-nav-item";
import { memo } from "react";
import { IntentOnlySidebarPromo } from "../../../../conversion/locked/intent-only-sidebar-promo";
import {
  CONVERSION_ICON,
  CONVERSION_ICON_FILLED,
  ConversionSuiteStatusesContext,
} from "../../../../redo-suite/conversion-cloud";
import { AbTestItems } from "./ab-tests";
import { AnalyticsItems } from "./analytics";
import { CheckoutItems } from "./checkouts";
import { DashboardItems } from "./dashboard";
import { DiscountRulesItems } from "./discount-rules";
import { OnboardingItems } from "./onboarding";
import { PostPurchaseItems } from "./post-purchase";
import { PricingTestItems } from "./pricing-tests";
import { SpotlightsItems } from "./spotlights";
import { WidgetsItems } from "./widgets";

export function useConversionNavigation(): PageNavigationEntry | null {
  const routes = useRequiredContext(MerchantRoutesContext);
  const team = useRequiredContext(TeamContext);
  const {
    checkoutOptimizationEnabled,
    checkoutOutboundRatesEnabled,
    accessLevel,
  } = useRequiredContext(ConversionSuiteStatusesContext);

  const canViewConversion = useHasPermission(
    ConversionPermissions.ACCESS_CONVERSION,
  );

  if (team.platform !== "shopify" || !canViewConversion) {
    return null;
  }

  const pathRoot = routes.conversionBaseUrl;
  // Intent-on tiers (intent-only / intent-only-small) and full get a secondary
  // nav. The intent-off tiers (intent-rescue-small / none) land on a welcome
  // page with no secondary nav.
  const isIntentOnTier =
    accessLevel === "intent-only" || accessLevel === "intent-only-small";
  const hasAnyAccess = accessLevel === "full" || isIntentOnTier;

  // Intent-on teams home to the dashboard (their nav + Spotlights live there);
  // everyone else uses the default checkout-optimization landing logic, which
  // routes intent-off teams to the welcome page.
  const conversionHref = isIntentOnTier
    ? routes.getConversionSubRoute("dashboard")
    : routes.getConversionHref({
        enabled: checkoutOptimizationEnabled,
        checkoutOutboundRatesEnabled,
      });

  const primaryTab: LinkPrimaryTab = {
    id: "conversion",
    hasNotification: false,
    UnfilledIcon: CONVERSION_ICON,
    FilledIcon: CONVERSION_ICON_FILLED,
    type: "link",
    href: conversionHref,
    pathRoot,
    friendlyName: "Conversion",
  };

  const secondaryNavigationData: SecondaryNavigationData | undefined =
    hasAnyAccess
      ? {
          id: "conversion",
          name: "Conversion",
          NavContent: SecondaryConversionSection,
        }
      : undefined;

  return { primaryTab, secondaryNavigationData, isActiveProduct: hasAnyAccess };
}

const SecondaryConversionSection = memo(function SecondaryConversionSection() {
  const { id: nodeId, TreeContext } = useNavTreeNode({
    friendlyName: "Conversion",
  });
  const { accessLevel } = useRequiredContext(ConversionSuiteStatusesContext);

  return (
    <TreeContext.Provider value={nodeId}>
      {/* Order differs by access level — intent-only branches promote
          Spotlights up next to Dashboard since the locked items (or, for
          intent-only-small, the absent items) shouldn't be the next thing the
          merchant's eye lands on. Full-suite keeps the historical order so
          existing merchants' muscle memory is undisturbed. */}
      {accessLevel === "intent-only-small" ? (
        // CRO-1430: small-deal-size self-serve teams don't get the locked nav
        // items or the upsell promo — sales doesn't want to chase them.
        <>
          <DashboardItems />
          <SpotlightsItems />
        </>
      ) : accessLevel === "intent-only" ? (
        <>
          <DashboardItems />
          <SpotlightsItems />
          <AbTestItems />
          <AnalyticsItems />
          <CheckoutItems />
          <DiscountRulesItems />
          <PostPurchaseItems />
          <PricingTestItems />
          <WidgetsItems />
          <IntentOnlySidebarPromo />
        </>
      ) : (
        <>
          <OnboardingItems />
          <DashboardItems />
          <AbTestItems />
          <AnalyticsItems />
          <CheckoutItems />
          <DiscountRulesItems />
          <PostPurchaseItems />
          <PricingTestItems />
          <SpotlightsItems />
          <WidgetsItems />
        </>
      )}
    </TreeContext.Provider>
  );
});
